import React, { useRef, useState, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import Button from "../../../ui/Button";
import logo from "../../../../assets/images/logo-rewind.png";
import AudioEditorNormalizeExportOptions from "../AudioEditorNormalizeExportOptions";
import toolbarStyles from "./AudioEditorToolbar.module.css";
import studio from "../../../modals/StudioModal.module.css";

interface AudioEditorExportModalProps {
    isOpen: boolean;
    onClose: () => void;
    onExport: (filename: string) => void;
    defaultFilename?: string;
}

const AudioEditorExportModal: React.FC<AudioEditorExportModalProps> = ({
    isOpen,
    onClose,
    onExport,
    defaultFilename = "export",
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const modalContentRef = useRef<HTMLDivElement>(null);
    const [filename, setFilename] = useState(defaultFilename);

    // Update local state when defaultFilename changes from context
    useEffect(() => {
        setFilename(defaultFilename);
    }, [defaultFilename]);

    const handleOutsideClick = useCallback(
        (e: MouseEvent) => {
            if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
                onClose();
            }
        },
        [onClose],
    );

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [isOpen, handleOutsideClick]);

    const handleSubmit = () => {
        // Remove any file extension the user might have added
        const baseFilename = filename.replace(/\.(mp3|wav)$/i, "");

        // Ensure we have a valid filename
        const validFilename = baseFilename.trim() || defaultFilename;

        onExport(validFilename);
        onClose();
    };

    if (!isOpen) return null;

    const modal = (
        <div className={studio.overlay}>
            <div className={`${studio.content} ${toolbarStyles.exportModalContent}`} ref={modalContentRef}>
                <div className={studio.logoWrap}>
                    <img src={logo} alt="Logo" />
                </div>
                <h1 className={studio.title}>{t("export")}</h1>

                <div className={toolbarStyles.exportModalInput}>
                    <label htmlFor="export-filename">{t("enterFilename")}</label>
                    <div className={toolbarStyles.filenameInputContainer}>
                        <input
                            id="export-filename"
                            type="text"
                            value={filename}
                            onChange={(e) => setFilename(e.target.value)}
                            autoFocus
                            placeholder={t("export")}
                        />
                        <span className={toolbarStyles.fileExtension}>.mp3</span>
                    </div>
                </div>

                <AudioEditorNormalizeExportOptions variant="export" />

                <div className={studio.buttonsCentered}>
                    <Button variant="cancel" label={t("cancel")} onClick={onClose} />
                    <Button variant="primary" label={t("download")} onClick={handleSubmit} />
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

export default AudioEditorExportModal;
